import type {
  Section,
  Step,
  Field as FieldFormality,
} from "@/types/entities/formality-request";
import type { FieldFile, Step as FormStep } from "@sdt/render-form-engine";

export function getAllFields(steps: readonly Step[]): FieldFormality[] {
  const out: FieldFormality[] = [];
  for (const step of steps) {
    const sections: readonly Section[] = step.sections ?? [];
    for (const section of sections) {
      const fields: FieldFormality[] = section.fields ?? [];
      out.push(...fields);
    }
  }
  return out;
}

export function resolveFilledFields(
  field: FormStep["sections"][number]["fields"][number],
  filledFieldsFromFormData: Map<string, FieldFormality["filled_fields"]>,
) {
  const fromMap = filledFieldsFromFormData.get(field.name);
  const currentFilled =
    (field.filled_fields as unknown as NonNullable<
      FieldFormality["filled_fields"]
    > | null) ?? [];
  const existingFilled =
    currentFilled.length > 0 ? currentFilled : (fromMap ?? []);
  const raw =
    field.type === "file" ? (fromMap ?? currentFilled) : existingFilled;

  // Virtual filled_fields come with `correction: null`, so the corrected one
  // can't be picked like lastWithCorrection does for regular fields. Every
  // save is kept: the corrections point to earlier ids, and the render form
  // engine resolves them against formAssignments and reads the last
  // input_field.length entries as the current values.
  if (field.type === "interoperability") {
    return raw ?? [];
  }

  const sorted = (raw ?? [])
    .filter((ff) => ff.value !== null && ff.value !== undefined)
    .sort(
      (a, b) =>
        new Date(a.updated_at ?? 0).getTime() -
        new Date(b.updated_at ?? 0).getTime(),
    );

  const lastWithCorrection = [...sorted].reverse().find((ff) => ff.correction);
  const best = [...sorted]
    .reverse()
    .find(
      (ff) => ff.value !== null && ff.value !== undefined && ff.value !== "",
    );

  const selected: FieldFormality["filled_fields"] = [];
  if (lastWithCorrection && lastWithCorrection.id !== best?.id) {
    selected.push(lastWithCorrection);
  }
  if (best) {
    selected.push(best);
  } else if (sorted.length > 0) {
    selected.push(sorted[sorted.length - 1]);
  }

  return selected;
}

export function prepopulateSection(
  section: FormStep["sections"][number],
  filledFieldsFromFormData: Map<string, FieldFormality["filled_fields"]>,
) {
  return {
    ...section,
    fields: (section.fields ?? []).map((field) => ({
      ...field,
      filled_fields: resolveFilledFields(
        field,
        filledFieldsFromFormData,
      ) as unknown as typeof field.filled_fields,
    })),
  };
}

export function prepopulateSteps(
  steps: FormStep[],
  filledFieldsFromFormData: Map<string, FieldFormality["filled_fields"]>,
): FormStep[] {
  return steps.map((step) => ({
    ...step,
    sections: (step.sections ?? []).map((section) =>
      prepopulateSection(section, filledFieldsFromFormData),
    ),
  }));
}

/**
 * Valor de un campo `file` en `current_values` de la asignación. `files` sólo
 * viene cuando el campo tiene archivos. Cada `url` es una firma de GCS que
 * caduca (60 min por defecto): no se persiste.
 */
interface CurrentFileValue {
  file_uuid?: string;
  file_uuids?: string[];
  files?: FieldFile[];
}

const isFieldFile = (value: unknown): value is FieldFile =>
  !!value &&
  typeof value === "object" &&
  typeof (value as FieldFile).id === "string" &&
  typeof (value as FieldFile).url === "string";

const getCurrentFiles = (value: unknown): FieldFile[] => {
  const files = (value as CurrentFileValue | null)?.files;
  return Array.isArray(files) ? files.filter(isFieldFile) : [];
};

type FilledFieldsMap = Map<string, FieldFormality["filled_fields"]>;

type FormField = FormStep["sections"][number]["fields"][number];

const getFileFields = (steps: readonly FormStep[]): FormField[] =>
  steps.flatMap((step) =>
    (step.sections ?? []).flatMap((section) =>
      (section.fields ?? []).filter((field) => field.type === "file"),
    ),
  );

const buildCurrentFileFilledField = (
  field: FormField,
  value: unknown,
  files: FieldFile[],
  selected: NonNullable<FieldFormality["filled_fields"]>[number] | undefined,
  filledFormId: string,
) => {
  if (selected) return { ...selected, files };
  const fileValue: CurrentFileValue = { ...(value as CurrentFileValue) };
  delete fileValue.files;
  return {
    id: `current-${field.id}`,
    value: fileValue,
    filled_form_id: filledFormId,
    field_id: field.id,
    created_at: "",
    updated_at: "",
    files,
  };
};

/**
 * Los archivos de cada campo `file` salen de `current_values`, el estado
 * actual del borrador: un campo con varios adjuntos puede repartirlos entre
 * varios filled_fields, y `resolveFilledFields` sólo conserva uno. Se mantiene
 * el filled_field existente (id, valor, corrección) y sólo se cambian sus
 * `files`. Sin `files` en `current_values` queda lo que había.
 * @param filledFields Los filled_fields por nombre de campo.
 * @param steps Los pasos del formulario de la asignación.
 * @param currentValues `current_values` de la asignación; el backend manda
 * `[]` cuando no hay valores.
 * @param filledFormId El `filled_form.id` de la asignación, para el
 * filled_field que se crea cuando el campo no tenía uno.
 */
export function applyCurrentFileValues(
  filledFields: FilledFieldsMap,
  steps: readonly FormStep[],
  currentValues: unknown,
  filledFormId = "",
): FilledFieldsMap {
  if (
    !currentValues ||
    typeof currentValues !== "object" ||
    Array.isArray(currentValues)
  ) {
    return filledFields;
  }
  const values = currentValues as Record<string, unknown>;

  const result = new Map(filledFields);
  for (const field of getFileFields(steps)) {
    const value = values[field.name];
    const files = getCurrentFiles(value);
    if (files.length === 0) continue;

    const [selected] = result.get(field.name) ?? [];
    result.set(field.name, [
      buildCurrentFileFilledField(field, value, files, selected, filledFormId),
    ] as unknown as FieldFormality["filled_fields"]);
  }
  return result;
}

export const removeFileFields = (
  data: Record<string, unknown>,
  fileFieldNames?: Set<string>,
) => {
  const result: Record<string, unknown> = {};

  Object.entries(data).forEach(([key, value]) => {
    if (fileFieldNames?.has(key)) return;

    if (value instanceof File) return;

    if (Array.isArray(value)) {
      if (value.some((v) => v instanceof File)) return;
      result[key] = value;
      return;
    }

    if (value && typeof value === "object") {
      result[key] = removeFileFields(value as Record<string, unknown>);
      return;
    }

    result[key] = value;
  });

  return result;
};
